import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Clipping } from 'src/app/components/image/model';
import { API, CompanyFullDTO, FixedAspectRatioImageDTO, SingleFileUpload } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { ImageUploadService } from 'src/app/services/gui/image/image-upload.service';
import { ImageUploadModel } from 'src/app/services/gui/image/model';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { UtilService } from 'src/app/services/util.service';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { NavigationService } from '@ngx-seatback/utils';
import { CompanyStatus } from '../../../services/global/global-interfaces';
import { CompanyFullDTOAndTags } from '../../../models/taggable.model';
import { TagService } from '../../../services/tag.service';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-company-details',
  templateUrl: './company-details.component.html',
  styleUrls: ['./company-details.component.scss'],
  standalone: false,
})
export class CompanyDetailsComponent implements OnInit, OnDestroy {
  companyId: number;
  company: CompanyFullDTOAndTags;

  avatarUrl: string = './assets/img/no-image.png';
  headerUrl: string = './assets/img/companydetails-bg.jpg';

  logoImageUpload: ImageUploadModel;
  headerImageUpload: ImageUploadModel;

  status: CompanyStatus[] = [];

  loading: boolean = true;

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    private modal: ModalService,
    private imageUploadService: ImageUploadService,
    public util: UtilService,
    public errorHandler: ErrorService,
    public translate: TranslateService,
    public navService: NavigationService,
    public tagService: TagService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.logoImageUpload = new ImageUploadModel();
    this.headerImageUpload = new ImageUploadModel();

    this.companyId = undefined;
    this.company = undefined;
    this.avatarUrl = './assets/img/no-image.png';
    this.headerUrl = './assets/img/companydetails-bg.jpg';
    this.status = [];
    this.loading = true;
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe(params => {
      this.companyId = +params.companyID;
      this.refreshFromServer();
    });
  }

  ngOnDestroy(): void {
    this.logoImageUpload?.destroySubscriptions();
    this.headerImageUpload?.destroySubscriptions();
  }

  refreshFromServer() {
    this.loading = true;
    this.api
      .shared_Company_retrieveCompanyById(this.companyId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (e: CompanyFullDTO) => {
          this.company = e;
          if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
            const companies = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.Company, [this.company]);
            this.company = companies[0];
          }

          this.avatarUrl = this.company?.logo
            ? this.util.appendQueryParamToUrl(this.company.logo.url as unknown as string, 'thumbnail=L')
            : './assets/img/no-image.png';
          this.headerUrl = this.company?.headerImage
            ? this.util.appendQueryParamToUrl(this.company.headerImage.url as unknown as string, '')
            : './assets/img/companydetails-bg.jpg';
          this.status = [];

          this.status.push({ type: 'hasCampaigns', value: this.company.countCampaigns > 0 });
          this.status.push({ type: 'hasMedia', value: this.company.countMedia > 0 || this.company.countMediumGroups > 0 });
          this.status.push({ type: 'hasAreas', value: this.company.countAreas > 0 });
          this.status.push({ type: 'hasCulls', value: this.company.countCulls > 0 });
          this.status.push({ type: 'hasLocations', value: this.company.countManagedLocations > 0 });
        },
        error: error => {
          if (error.status && (+error.status === 403 || +error.status === 404)) {
            this.navService.navigateToPoint(RoutingID.companies);
          } else {
            this.errorHandler.errorHandler(error);
          }
        },
      });
  }

  applyClipping(imageUpload: ImageUploadModel, res: Clipping) {
    imageUpload.imageTransfer.image.clippingTLX = res.clippingTLX;
    imageUpload.imageTransfer.image.clippingTLY = res.clippingTLY;
    imageUpload.imageTransfer.image.clippingBRX = res.clippingBRX;
    imageUpload.imageTransfer.image.clippingBRY = res.clippingBRY;
  }

  pickLogo() {
    this.logoImageUpload?.destroySubscriptions();
    this.api
      .shared_Company_retrieveCompanyById(this.companyId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (company: CompanyFullDTO) => {
          this.logoImageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.logo'));
          this.logoImageUpload.imageTransfer.image = company?.logo;
          this.logoImageUpload.subClippingChange = this.logoImageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.logoClippingChange(res);
          });
          this.logoImageUpload.subFileChange = this.logoImageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.logoFileChange(file);
          });
          this.logoImageUpload.subDeletePress = this.logoImageUpload.imageTransfer.deletePress.subscribe(() => {
            this.logoDeletePress();
          });
          this.logoImageUpload.subModalClosed = this.logoImageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.logoUploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  logoClippingChange(res: Clipping) {
    if (res) {
      this.applyClipping(this.logoImageUpload, res);
      const transImage: FixedAspectRatioImageDTO = this.logoImageUpload.imageTransfer.image;
      this.logoImageUpload.imageTransfer.image = null;
      this.logoImageUpload.imageTransfer.uploading = true;
      this.api
        .shared_Company_clipLogo(this.companyId, transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (image: FixedAspectRatioImageDTO) => {
            this.logoImageUpload.imageTransfer.image = image;
            this.logoImageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  logoFileChange(file: SingleFileUpload) {
    this.logoImageUpload.imageTransfer.image = null;
    this.logoImageUpload.imageTransfer.uploading = true;
    this.api
      .shared_Company_uploadLogo(this.companyId, file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: FixedAspectRatioImageDTO) => {
          this.logoImageUpload.imageTransfer.image = res;
          this.logoImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  logoDeletePress() {
    this.logoImageUpload.imageTransfer.image = null;
    this.logoImageUpload.imageTransfer.uploading = true;
    this.api
      .shared_Company_deleteLogo(this.companyId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.logoImageUpload.imageTransfer.image = null;
          }
          this.logoImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  logoUploadModalClosed() {
    this.logoImageUpload.destroySubscriptions();
    this.refreshFromServer();
  }

  pickHeader() {
    this.headerImageUpload?.destroySubscriptions();
    this.api
      .shared_Company_retrieveCompanyById(this.companyId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (company: CompanyFullDTO) => {
          this.headerImageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.header'));
          this.headerImageUpload.imageTransfer.image = company?.headerImage;
          this.headerImageUpload.subClippingChange = this.headerImageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.headerClippingChange(res);
          });
          this.headerImageUpload.subFileChange = this.headerImageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.headerFileChange(file);
          });
          this.headerImageUpload.subDeletePress = this.headerImageUpload.imageTransfer.deletePress.subscribe(() => {
            this.headerDeletePress();
          });
          this.headerImageUpload.subModalClosed = this.headerImageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.headerUploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  headerClippingChange(res: Clipping) {
    if (res) {
      this.applyClipping(this.headerImageUpload, res);
      const transImage: FixedAspectRatioImageDTO = this.headerImageUpload.imageTransfer.image;
      this.headerImageUpload.imageTransfer.image = null;
      this.headerImageUpload.imageTransfer.uploading = true;
      this.api
        .shared_Company_clipHeaderImage(this.companyId, transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (image: FixedAspectRatioImageDTO) => {
            this.headerImageUpload.imageTransfer.image = image;
            this.headerImageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  headerFileChange(file: SingleFileUpload) {
    this.headerImageUpload.imageTransfer.image = null;
    this.headerImageUpload.imageTransfer.uploading = true;
    this.api
      .shared_Company_uploadHeaderImage(this.companyId, file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: FixedAspectRatioImageDTO) => {
          this.headerImageUpload.imageTransfer.image = res;
          this.headerImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  headerDeletePress() {
    this.headerImageUpload.imageTransfer.image = null;
    this.headerImageUpload.imageTransfer.uploading = true;
    this.api
      .shared_Company_deleteHeaderImage(this.companyId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.headerImageUpload.imageTransfer.image = null;
          }
          this.headerImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  headerUploadModalClosed() {
    this.headerImageUpload.destroySubscriptions();
    this.refreshFromServer();
  }
}
